import { Box, Button } from "@mui/material"
import { Upload } from "lucide-react"
import { useState, type JSX } from "react"
import { useNavigate } from "react-router-dom"

import { useCan } from "@/auth/permissions"
import { useToast } from "@/components/ui/use-toast"

import { PurchaseOrderDialog } from "./purchase-order-dialog"
import { PurchaseOrdersList } from "./purchase-orders-list"

export type PurchaseOrdersTabProps =
  | { customerId: string; customerName: string; resellerId?: never }
  | { resellerId: string; customerId?: never; customerName?: never }

/**
 * The Purchase Orders tab of a customer or a reseller profile: the ONE
 * purchase-order list, narrowed to this company, plus an Upload button that
 * opens the ordinary upload dialog already assigned to it.
 *
 * A money tab — the profile pages filter it out of the tab set without
 * financial access (like Quotes and Invoices), so this never mounts for such a
 * viewer; every figure inside still goes through `<Money>`.
 */
export function PurchaseOrdersTab(props: PurchaseOrdersTabProps): JSX.Element {
  const navigate = useNavigate()
  const { toast } = useToast()
  const canEdit = useCan("crm:edit")
  const [open, setOpen] = useState(false)

  const forCustomer = props.customerId !== undefined

  return (
    <Box data-cy={forCustomer ? "customer-purchase-orders-tab" : "reseller-purchase-orders-tab"} sx={{ display: "grid", gap: 2 }}>
      {canEdit && (
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button size="small" variant="contained" startIcon={<Upload size={16} />} onClick={() => setOpen(true)} data-cy="po-tab-upload">
            Upload purchase order
          </Button>
        </Box>
      )}

      <PurchaseOrdersList
        {...(forCustomer ? { customerId: props.customerId } : { resellerId: props.resellerId })}
        emptyMessage={forCustomer ? "This customer has not sent a purchase order yet." : "This reseller has not sent a purchase order yet."}
      />

      {canEdit && (
        <PurchaseOrderDialog
          open={open}
          onClose={() => setOpen(false)}
          {...(forCustomer
            ? { defaultCustomer: { id: props.customerId as string, name: props.customerName as string } }
            : { defaultResellerId: props.resellerId as string })}
          onSaved={(po) => {
            setOpen(false)
            toast({ title: "Purchase order uploaded", description: `${po.number} was recorded.` })
            navigate(`/crm/purchase-orders/${po.id}`)
          }}
        />
      )}
    </Box>
  )
}
